import { CalendarDays } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface DateRange {
  /** ISO yyyy-mm-dd or '' when unset */
  from: string;
  to: string;
}

export interface DateRangePreset {
  label: string;
  /** Built lazily so "this month" is resolved when clicked, not at render. */
  range: () => DateRange;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  label?: string;
  /**
   * Shortcuts rendered INSIDE the control. A filter bar that puts "This month /
   * Last month" in a separate right-aligned row leaves them orphaned from the
   * field they act on — they belong to the range, so they live with it.
   */
  presets?: DateRangePreset[];
  className?: string;
}

/**
 * Date-range picker over native date inputs — dependable across browsers;
 * upgrade to a popover calendar later without changing the API.
 *
 * The two inputs are styled to belong to this app rather than to the browser:
 * an unset field renders its `mm/dd/yyyy` in placeholder grey (so it reads as
 * empty, not as a value), a set one in ink, and the picker glyph is toned down
 * to match the rest of the chrome.
 */
export function DateRangePicker({
  value,
  onChange,
  label,
  presets,
  className,
}: DateRangePickerProps) {
  const fieldClass = (set: boolean) =>
    cn(
      'num bg-transparent text-sm focus:outline-none',
      // Native date inputs print "mm/dd/yyyy" when empty. Greyed, that is a
      // placeholder; in body ink it looked like unstyled browser chrome sitting
      // in the middle of a tightly styled filter bar.
      set ? 'text-secondary' : 'text-grey-400',
      '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
      '[&::-webkit-calendar-picker-indicator]:opacity-40',
      '[&::-webkit-calendar-picker-indicator]:hover:opacity-80',
    );

  return (
    <div className={className}>
      {label && <span className="label">{label}</span>}
      <div
        className={cn(
          'inline-flex flex-wrap items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 py-1.5',
          'focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20 transition-all duration-200',
        )}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
        <input
          type="date"
          aria-label="Start date"
          value={value.from}
          max={value.to || undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
          className={fieldClass(!!value.from)}
        />
        <span className="text-grey-400">–</span>
        <input
          type="date"
          aria-label="End date"
          value={value.to}
          min={value.from || undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
          className={fieldClass(!!value.to)}
        />
        {presets && presets.length > 0 && (
          <span className="flex items-center gap-1 border-l border-grey-200 pl-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => onChange(preset.range())}
                className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary"
              >
                {preset.label}
              </button>
            ))}
            {(value.from || value.to) && (
              <button
                type="button"
                onClick={() => onChange({ from: '', to: '' })}
                aria-label="Clear date range"
                className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
              >
                Any
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
